'use client';

import type { AppSettings } from '@/utils/data/fetch-app-settings';
import { LOGIN_SOURCE, TERMS_OF_USE_URL, PRIVACY_TERMS_URL } from '@/constants';
import {
	LoginFormStatus,
	type LoginFormProps,
} from '@/components/raven/global/LoginForm/types';
import {
	useMemo,
	useState,
	useEffect,
	useCallback,
	type FormEvent,
	type ChangeEvent,
} from 'react';
import {
	OAuthPhase,
	EmailLinkPhase,
	useLoginContext,
	type OAuthProviderType,
} from '@/context/LoginContext';

export interface UseLoginFormProps {
	errorMessage?: null | string;
	location?: string;
	loginKey?: string;
	message?: string;
	onEmailLinkPhaseCompleted?: () => void;
	settings?: AppSettings['settings'];
	usePasswordLogin?: boolean;
}

export const useDefaultCheckboxText = (
	checkboxTextProp?: JSX.Element | string,
) =>
	useMemo(() => {
		return (
			checkboxTextProp || (
				<>
					By signing up, I agree to the{' '}
					<a href={TERMS_OF_USE_URL} rel="noopener noreferrer" target="_blank">
						{' '}
						Terms of Use
					</a>{' '}
					and acknowledge that I have read the{' '}
					<a href={PRIVACY_TERMS_URL} rel="noopener noreferrer" target="_blank">
						Privacy Policy
					</a>
					. I understand that I may unsubscribe from email communications at any
					time.
				</>
			)
		);
	}, [checkboxTextProp]);

export const useHandleSubmit = (onContinue?: (email: string) => void) =>
	useCallback(
		(e: FormEvent<HTMLFormElement>) => {
			e.preventDefault();
			if (e.currentTarget.checkValidity()) {
				const formData = new FormData(e.currentTarget);
				const email = formData.get('email');
				onContinue?.(email?.toString() || '');
			}
		},
		[onContinue],
	);

export const useHandleSignInWithPassword = (
	onPasswordLoginContinue?: (email: string, password: string) => void,
) =>
	useCallback(
		(e: FormEvent<HTMLFormElement>) => {
			e.preventDefault();
			if (e.currentTarget.checkValidity()) {
				const formData = new FormData(e.currentTarget);
				const emailValue = formData.get('email');
				const password = formData.get('password');
				onPasswordLoginContinue?.(
					emailValue?.toString() || '',
					password?.toString() || '',
				);
			}
		},
		[onPasswordLoginContinue],
	);

export const useLoginFormProps: (
	props: UseLoginFormProps,
) => LoginFormProps = ({
	errorMessage,
	location,
	loginKey = '',
	message,
	onEmailLinkPhaseCompleted,
	settings,
	usePasswordLogin,
}) => {
	const [email, setEmail] = useState('');
	const {
		afterCompleteLoginFnRef,
		emailLinkPhase,
		handleAfterFirebaseLogin,
		handleSendEmailLink,
		handleSendPasswordResetLink,
		handleSignInWithPassword,
		isLoading,
		oauthPhase,
		setStatus,
		signInOAuth,
		status,
	} = useLoginContext();

	useEffect(() => {
		switch (oauthPhase) {
			case OAuthPhase.CHOOSE:
				setStatus(LoginFormStatus.IDLE);
				break;
			case OAuthPhase.SIGNING_IN:
				setStatus(LoginFormStatus.LOADING);
				break;
			default:
		}
		switch (emailLinkPhase) {
			case EmailLinkPhase.COMPLETED:
				setStatus(LoginFormStatus.SUCCESS);
				if (onEmailLinkPhaseCompleted) {
					onEmailLinkPhaseCompleted();
				}
				break;
			case EmailLinkPhase.CONFIRM:
			case EmailLinkPhase.SENT:
				setStatus(LoginFormStatus.SUCCESS);
				break;
			case EmailLinkPhase.INPUT:
				setStatus(LoginFormStatus.IDLE);
				break;
			case EmailLinkPhase.SIGNING_IN:
				setStatus(LoginFormStatus.LOADING);
				break;
			default:
		}
	}, [emailLinkPhase, oauthPhase, onEmailLinkPhaseCompleted, setStatus]);

	useEffect(() => {
		const searchParams = new URLSearchParams(window.location.search);
		if (
			searchParams.get('apiKey') &&
			searchParams.get(LOGIN_SOURCE) === loginKey
		) {
			handleAfterFirebaseLogin();
		}
	}, [handleAfterFirebaseLogin, loginKey, setStatus]);

	useEffect(() => {
		afterCompleteLoginFnRef.current = () => {
			const targetUrl = window.location.href;
			window.history.replaceState(
				{},
				document.title,
				targetUrl.replace(window.location.search, ''),
			);
		};
	}, [afterCompleteLoginFnRef]);

	const handleOAuthSignIn = useCallback(
		(social: OAuthProviderType) => {
			setStatus(LoginFormStatus.IDLE);
			signInOAuth(social, location);
		},
		[location, setStatus, signInOAuth],
	);

	const onLoginFormContinue = useCallback(async () => {
		const loginUrl = new URL(window.location.href);
		if (loginKey) {
			loginUrl.searchParams.set(LOGIN_SOURCE, loginKey);
		}
		setStatus(LoginFormStatus.LOADING);
		await handleSendEmailLink(email, loginUrl.toString(), location);
	}, [email, handleSendEmailLink, location, loginKey, setStatus]);

	const onEmailChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
		setEmail(e.target.value);
	}, []);

	return useMemo<LoginFormProps>(
		() => ({
			descriptionDefault: message || 'Join the conversation',
			descriptionError:
				errorMessage || 'Please try again or use a different sign in method.',
			descriptionLoading: 'Please wait while we sign you in.',
			descriptionSuccess:
				emailLinkPhase === EmailLinkPhase.SENT
					? `Check your email ${email} to complete the sign in process. If it's missing, check spam or request a new one.`
					: message,
			handleOAuthSignIn,
			location,
			onContinue: onLoginFormContinue,
			onEmailChange,
			onPasswordLoginContinue: handleSignInWithPassword,
			onPasswordResetContinue: handleSendPasswordResetLink,
			settings: settings || ({} as AppSettings['settings']),
			status: isLoading ? LoginFormStatus.LOADING : status,
			textFieldPlaceholder: 'Enter your email',
			titleDefault: 'Sign in to continue',
			titleError: 'Sign in failed',
			titleLoading: 'Signing in...',
			titleSuccess: "You're almost there!",
			usePasswordLogin,
		}),
		[
			message,
			errorMessage,
			emailLinkPhase,
			email,
			handleOAuthSignIn,
			location,
			onLoginFormContinue,
			onEmailChange,
			handleSignInWithPassword,
			handleSendPasswordResetLink,
			settings,
			isLoading,
			status,
			usePasswordLogin,
		],
	);
};
